import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { EnumToOptions } from 'helpers/CreditOnCardHelpers.tsx'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useUpdateCustomerCardAccount } from '../../api/react-query'
import { AccountStatusPatch, type BackOfficeAccountGetDto } from '../../api/swagger/definitions/backoffice'
import { CoreMyntModal } from '../../components/modals/CoreMyntModal.tsx'
import { SelectController } from '../../components/react-hook-components.tsx'

interface CustomerCardTabChangeStatusModalProps {
  isOpen: boolean
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  cardAccount: BackOfficeAccountGetDto
  customerId: string
  onSuccess?: () => void
}

const CustomerCardTabChangeStatusModal: React.FC<CustomerCardTabChangeStatusModalProps> = ({
  isOpen,
  setIsModalOpen,
  onSuccess,
  cardAccount,
  customerId,
}) => {
  const cardAccountMutation = useUpdateCustomerCardAccount(customerId)
  const [submitting, setSubmitting] = useState(false)

  const form = useForm({
    defaultValues: {
      accountStatus: cardAccount?.status as AccountStatusPatch,
    },
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore existing biome errors
  useEffect(() => {
    if (!cardAccount) return

    form.reset({ accountStatus: cardAccount.status as AccountStatusPatch })
  }, [cardAccount])

  const setAccountStatus = async (status: AccountStatusPatch) => {
    setSubmitting(true)
    await cardAccountMutation.mutateAsync({ status, id: cardAccount.id })

    setTimeout(() => {
      setSubmitting(false)
      setIsModalOpen(false)
      onSuccess && onSuccess()
    }, 300)
  }

  return (
    <CoreMyntModal
      onClose={() => setIsModalOpen(false)}
      open={isOpen}
      title="Change account status"
      actions={(Wrapper) => (
        <Wrapper>
          <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
          <Button
            onClick={form.handleSubmit(({ accountStatus }) => setAccountStatus(accountStatus))}
            disabled={submitting}
            loading={submitting}
            variant="contained"
          >
            Save
          </Button>
        </Wrapper>
      )}
    >
      <Box sx={{ paddingTop: '16px' }}>
        <SelectController
          control={form.control}
          name="accountStatus"
          labelText="Account status"
          options={EnumToOptions(AccountStatusPatch)}
        />
      </Box>
    </CoreMyntModal>
  )
}

export default CustomerCardTabChangeStatusModal
